import React, { useEffect, useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import { useBrand } from '../../contexts/BrandContext';
import { EinstellungsZeile } from '../../pages/einstellungen/EinstellungsZeile';
import { ansprechpersonen as ladeAnsprechpersonen, type KiAnsprechperson } from './hilfeApi';
import { ANSPRECHPERSON } from './hilfeTexte';

// Ansprechperson für KI (Profil-Nachbau F-20 Ergänzung 2, BAU-HILFE.md 9.2 Punkt 4; PF-321-2): je Schlüsselkontakt der Firma mit
// der Zuständigkeit „KI-Verantwortliche:r“ eine Zeile im Abschnitt „Selbst nicht weitergekommen?“ von „Dokumente“, rechts
// „E-Mail schreiben“ und, falls hinterlegt, „Anrufen“. Die Zeile ist eine Ergänzung: beim Laden, bei einem Fehler und ohne
// Kontakte steht nichts. F-24: Dokumente lädt die Personen, damit der Abschnitt ohne Inhalt entfällt.

/** mailto: mit kodierter Adresse. */
export const mailtoLink = (email: string) => `mailto:${encodeURIComponent(email)}`;

/** tel: mit nur Ziffern und einem führenden „+“; ohne Telefon oder ohne Ziffern null (kein Link). */
export function telefonLink(telefon: string | null): string | null {
  if (!telefon) return null;
  const ziffern = telefon.replace(/\D/g, '');
  if (!ziffern) return null;
  return `tel:${telefon.trim().startsWith('+') ? '+' : ''}${ziffern}`;
}

/** Eine Zeile; der Titel des Links nennt Adresse bzw. Nummer, wie gespeichert. */
export function AnsprechpersonZeile({ person, organisation }: { person: KiAnsprechperson; organisation: string }) {
  const anruf = telefonLink(person.telefon);
  return <EinstellungsZeile ebene={3} titel={ANSPRECHPERSON.titel(organisation)} erklaerung={ANSPRECHPERSON.erklaerung(person.name)}>
    <a className="adm-button" href={mailtoLink(person.email)} title={person.email}><Mail size={16} aria-hidden="true" />{ANSPRECHPERSON.mail}</a>
    {anruf && <a className="adm-button" href={anruf} title={person.telefon ?? undefined}><Phone size={16} aria-hidden="true" />{ANSPRECHPERSON.anrufen}</a>}
  </EinstellungsZeile>;
}

/** Die Ansprechpersonen der Firma, einmal geladen; beim Laden und bei einem Fehler leer. */
export function useAnsprechpersonen(): KiAnsprechperson[] {
  const [personen, setPersonen] = useState<KiAnsprechperson[]>([]);

  useEffect(() => {
    const abbruch = new AbortController();
    ladeAnsprechpersonen(abbruch.signal)
      .then(daten => { if (!abbruch.signal.aborted) setPersonen(daten.personen); })
      .catch(() => undefined);
    return () => abbruch.abort();
  }, []);

  return personen;
}

export default function Ansprechperson({ personen }: { personen: KiAnsprechperson[] }) {
  const brand = useBrand();
  return <>{personen.map((person, index) => <AnsprechpersonZeile key={`${person.email}-${index}`} person={person} organisation={brand.tenantLabel} />)}</>;
}
